import * as React from "react";
import { Button, CircularProgress, Slide, styled } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { JsonObject } from "fe-common/graphql";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { ClinicThemeColorPicker, ReactColor } from "./clinicThemeColorPicker";
import { scrollableStyle } from "fe-common/ui/commonStyles";

//region [[ Styles ]]

const ClinicThemeCustomizationView = styled("div")({
  display: "flex",
  flexFlow: "row",
  marginTop: "10px",
});

const PendingChangesContainer = styled("div")({
  margin: "5px",
  justifyContent: "center",
  alignItems: "center",
  background: "transparent",
  height: "0",
  width: "0",
  transition: "all 0.8s ease-out",
  visibility: "hidden",
  "&.show": {
    visibility: "visible",
    padding: "10px",
    height: "56px",
    border: "1px dashed orange",
    background: "bisque",
    width: "100%",
  },
});

const ClinicViewContainer = styled("div")({
  flex: 1,
  height: "calc(90vh - 30px)",
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  "& .chat-container.fullscreen": {
    position: "absolute",
    margin: "0 5px",
    width: "100%",
  },
  "& .kahun-container.fullscreen.open": {
    background: "transparent",
  },
});

const ColorsPickerArea = styled("div")({
  flex: 1,
  margin: "20px",
  display: "flex",
  flexFlow: "column",
  maxHeight: "80vh",
  ...scrollableStyle,
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface ClinicThemeCustomizationProps {
  clinicId: string;
  onSave: (newValue: JsonObject) => void;
  defaultTheme: any; // TODO: add a robust type for themes
}

//endregion [[ Props ]]

//region [[ Functions ]]
enum MainColors {
  Primary,
  Default,
  Secondary,
}

// Translates Primary to "--primary"
const mainColorToColorKey = (mainColor: MainColors | string) => {
  console.log(mainColor, Object.keys(MainColors));
  return `--${mainColor.toString().toLowerCase()}`;
};
// Object.keys also returns the number entries, filter them out
const enumKeys = (e_num) =>
  Object.keys(e_num).filter((eKey) => isNaN(Number(eKey)));
//endregion [[ Functions ]]

export const ClinicThemeCustomization = ({
  clinicId,
  onSave,
  defaultTheme,
}: ClinicThemeCustomizationProps) => {
  const [loading, setLoading] = useState(true);

  const [showMore, setShowAll] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  const [stateColors, setStateColors] = useState({});

  useEffect(() => {
    window["PUBLIC_URL"] = "https://patient-dev.kahun.com";
    let body = document.body;
    let script = document.createElement("script");
    script.type = "text/javascript";
    script.src = window["PUBLIC_URL"] + "/embed.js";
    body.appendChild(script);
  }, []);

  const handleColorSelected = useCallback((color: ReactColor, key: string) => {
    const p1stContainer = document.getElementsByClassName(
      "kahun-main-layout-view",
    );
    for (let i = 0; i < p1stContainer.length; i++) {
      const container = p1stContainer[i] as HTMLElement;
      container.style.setProperty(key, color.hex);
    }
  }, []);

  const createStateColorChangeHandler = useCallback(
    (colorKey: string) => (color: ReactColor) => {
      setIsDirty(true);
      setStateColors((currentColors) => ({
        ...currentColors,
        [colorKey]: color.hex,
      }));
      handleColorSelected(color, colorKey);
    },
    [setStateColors, handleColorSelected, setIsDirty],
  );

  const handleSaveThemeClicked = useCallback(() => {
    onSave((prev) => ({...prev, colors: { ...stateColors }}));
    setIsDirty(false);
  }, [onSave, setIsDirty, stateColors]);

  const generateColorSpecFromMainColors = () => {
    // To generate the new color spec we:
    //        1. Convert to HSL which, which by its Hue value determines the change in color
    //        2. For each color - look for the closest color to him in the original main colors
    //        3. Calculate the diff from this old main color to the new main color and apply it to the colors hue value
  };

  return (
    <ClinicThemeCustomizationView>
      <ClinicViewContainer>
        {loading && <CircularProgress />}
        <div
          id={"kahun-patient"}
          //@ts-ignore
          settings={JSON.stringify({ open: true, clinicId })}
          data-window-alignment={"fullscreen"}
        />
      </ClinicViewContainer>
      <ColorsPickerArea className={"sketch-color-picker-container"}>
        <PendingChangesContainer className={isDirty ? "show" : ""}>
          You are currently viewing a preview, to apply your theme changes to
          your clinic please click
          <Button onClick={handleSaveThemeClicked}>Save</Button>
        </PendingChangesContainer>
        {enumKeys(MainColors)
          .map(mainColorToColorKey)
          .map((colorKey) => (
            <ClinicThemeColorPicker
              key={colorKey}
              colorKey={colorKey}
              color={stateColors[colorKey]}
              onColorChange={createStateColorChangeHandler(colorKey)}
            />
          ))}
        <Button onClick={generateColorSpecFromMainColors}>
          Auto Generate Colors
        </Button>
        <Button onClick={() => setShowAll((o) => !o)}>
          Show {showMore ? "only main colors" : "all colors"}
        </Button>
        <Slide direction={"up"} in={showMore} mountOnEnter unmountOnExit>
          <div>
            <b>You can manually change the following colors:</b>
            {Object.keys(stateColors || {})
              ?.filter(
                (stateColorKey) =>
                  !enumKeys(MainColors).some(
                    (enumColorName) =>
                      mainColorToColorKey(enumColorName) === stateColorKey,
                  ),
              )
              .map((colorKey) => (
                <ClinicThemeColorPicker
                  key={colorKey}
                  colorKey={colorKey}
                  color={stateColors[colorKey] || ""}
                  onColorChange={createStateColorChangeHandler(colorKey)}
                />
              ))}
          </div>
        </Slide>
      </ColorsPickerArea>
    </ClinicThemeCustomizationView>
  );
};
